import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Container } from '@/components/container';
import { textFieldValidation } from '@/utils/validations';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import {
  IEventFormData,
  IEventTranslation,
} from '@/types/community-management.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import {
  useCreateEventMutation,
  useGetEventByIdQuery,
  useUpdateEventMutation,
  useTranslateEventContentMutation,
} from '@/services/CommunityManagementService';
import EventPreviewModal from './EventPreviewModal';
import EventRsvpList from './EventRsvpList';
import MediaUploadModal from '@/pages/habor-management/MediaUploadModal';
import { ImageCaptionDTO } from '@/types/api.types';
import NumberInput from '@/components/inputControls/NumberInput';

interface EventFormProps {
  mode: 'create' | 'edit';
}

const EventForm = ({ mode }: EventFormProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();

  // Redux state
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );

  // Mode and route variables
  const isEdit = mode === 'edit';
  const eventId = isEdit ? params.id : undefined;
  const isViewMode = searchParams.get('mode') === 'view';
  const isReadOnly = isViewMode;

  // API mutations
  const [createEvent] = useCreateEventMutation();
  const [updateEvent] = useUpdateEventMutation();
  const [translateEventContent] = useTranslateEventContentMutation();

  // API query for fetching event data in edit mode
  const {
    data: eventData,
    isLoading: isEventLoading,
    isError: isEventError,
  } = useGetEventByIdQuery(
    {
      communityId: communityId || '',
      eventId: eventId || '',
    },
    {
      skip: !isEdit || !eventId || !communityId,
    }
  );

  // Translation state
  const [translations, setTranslations] = useState<
    Record<string, IEventTranslation>
  >({});

  // Preview state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [showPreviewMessage, setShowPreviewMessage] = useState(false);

  const [formData, setFormData] = useState<Partial<IEventFormData>>({});
  const [originalEventData, setOriginalEventData] = useState<
    Partial<IEventFormData>
  >({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [mediaPreview, setMediaPreview] = useState<string[]>([]);
  const [removedMediaUrls, setRemovedMediaUrls] = useState<string[]>([]);
  const [mediaItems, setMediaItems] = useState<(File | string)[]>([]);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [imageCaptions, setImageCaptions] = useState<ImageCaptionDTO[]>([]);

  // Check if content has been modified from original
  const contentHasChanged = useMemo(() => {
    if (!isEdit || !originalEventData.title) return false;

    const hasChanged =
      formData.title !== originalEventData.title ||
      formData.description !== originalEventData.description ||
      formData.location_text !== originalEventData.location_text;

    return hasChanged;
  }, [
    isEdit,
    formData.title,
    formData.description,
    formData.location_text,
    originalEventData,
  ]);

  // Load event data when in edit mode
  useEffect(() => {
    if (eventData) {
      const eventFormData = {
        id: eventData.id,
        title: eventData.title,
        description: eventData.description,
        location_text: eventData.location_text,
        latitude: eventData.latitude,
        longitude: eventData.longitude,
        start_at: eventData.start_at,
        end_at: eventData.end_at,
        timezone: eventData.timezone,
        status: eventData.status,
        language: eventData.language,
        tags: eventData.tags,
        translations: eventData.translations,
      };

      // Set form data with actual event data
      setFormData(eventFormData);

      // Store original event data for comparison
      setOriginalEventData({
        title: eventData.title,
        description: eventData.description,
        location_text: eventData.location_text,
      });

      // Seed translations state from existing event translations
      if (eventData.translations) {
        setTranslations(eventData.translations);
      }

      // Set existing banner images
      if (eventData.banner_images && eventData.banner_images.length > 0) {
        setMediaPreview(eventData.banner_images);
        setMediaItems(eventData.banner_images);
      }

      // Set existing captions
      if (eventData.image_captions) {
        const mappedCaptions = eventData.image_captions.map((caption) => ({
          image_index: caption.image_index,
          captions: caption.translations,
        }));
        setImageCaptions(mappedCaptions);
      }
    }
  }, [eventData]);

  const createValidationSchema = useCallback(() => {
    return Yup.object().shape({
      title: textFieldValidation(
        intl ? intl.formatMessage({ id: 'EVENT.FORM.TITLE' }) : 'Event Title',
        2,
        100,
        true,
        intl
      ),
      description: Yup.string()
        .required(
          intl
            ? intl.formatMessage({
              id: 'EVENT.VALIDATION.DESCRIPTION_REQUIRED',
            })
            : 'Description is required'
        )
        .min(
          10,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.DESCRIPTION_MIN' })
            : 'Description must be at least 10 characters'
        ),
      location_text: textFieldValidation(
        intl ? intl.formatMessage({ id: 'EVENT.FORM.LOCATION' }) : 'Location',
        2,
        200,
        true,
        intl
      ),
      latitude: Yup.number()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return null;
          }

          if (typeof originalValue === 'string') {
            const normalized = originalValue
              .replace(/\s/g, '')
              .replace(',', '.');
            const parsed = Number(normalized);
            return Number.isNaN(parsed) ? NaN : parsed;
          }

          return value;
        })
        .nullable()
        .notRequired()
        .typeError(
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LATITUDE_NUMBER' })
            : 'Latitude must be a number'
        )
        .min(
          -90,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LATITUDE_RANGE' })
            : 'Latitude must be between -90 and 90'
        )
        .max(
          90,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LATITUDE_RANGE' })
            : 'Latitude must be between -90 and 90'
        ),
      longitude: Yup.number()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined
          ) {
            return null;
          }

          if (typeof originalValue === 'string') {
            const normalized = originalValue
              .replace(/\s/g, '')
              .replace(',', '.');
            const parsed = Number(normalized);
            return Number.isNaN(parsed) ? NaN : parsed;
          }

          return value;
        })
        .nullable()
        .notRequired()
        .typeError(
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LONGITUDE_NUMBER' })
            : 'Longitude must be a number'
        )
        .min(
          -180,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LONGITUDE_RANGE' })
            : 'Longitude must be between -180 and 180'
        )
        .max(
          180,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.LONGITUDE_RANGE' })
            : 'Longitude must be between -180 and 180'
        ),
      tags: Yup.array()
        .of(
          Yup.string().max(
            50,
            intl
              ? intl.formatMessage({ id: 'EVENT.VALIDATION.TAG_MAX' })
              : 'Tag cannot exceed 50 characters'
          )
        )
        .max(
          10,
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.TAGS_MAX' })
            : 'Maximum 10 tags allowed'
        )
        .nullable()
        .notRequired(),
      start_at: Yup.string().required(
        intl
          ? intl.formatMessage({ id: 'EVENT.VALIDATION.START_DATE_REQUIRED' })
          : 'Start date is required'
      ),
      end_at: Yup.string()
        .required(
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.END_DATE_REQUIRED' })
            : 'End date is required'
        )
        .test(
          'is-after-start',
          intl
            ? intl.formatMessage({ id: 'EVENT.VALIDATION.END_AFTER_START' })
            : 'End date must be after start date',
          function (value) {
            const { start_at } = this.parent;
            if (!start_at || !value) return true;
            return new Date(value) >= new Date(start_at);
          }
        ),
    });
  }, [intl]);

  // Validate individual field
  const validateField = useCallback(
    async (name: string, value: unknown) => {
      try {
        const schema = createValidationSchema();
        const fieldSchema = Yup.reach(schema, name);
        await (fieldSchema as Yup.AnySchema).validate(value);

        // Clear error if validation passes
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
        return true;
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: error.message }));
          return false;
        }
        return false;
      }
    },
    [createValidationSchema]
  );

  const handleInputChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      const { name, value } = e.target;
      let parsedValue: string | number | Date = value;

      // Parse date fields
      if (name.includes('_at')) {
        parsedValue = value ? new Date(value).toISOString() : '';
      }

      // Parse numeric fields
      if (name === 'latitude' || name === 'longitude') {
        parsedValue = value ? parseFloat(value) : '';
      }

      setFormData((prev) => ({ ...prev, [name]: parsedValue }));
      // Clear error for this field when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    },
    [setFormData, errors]
  );

  const handleInputBlur = useCallback(
    async (name: string, value: string | number | Date) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      // Validate field on blur
      await validateField(name, value);
    },
    [validateField]
  );

  const handleTagAdd = useCallback(() => {
    const trimmed = tagInput.trim();
    if (!trimmed) return;

    setFormData((prev) => {
      const existingTags = (prev.tags || []) as string[];
      if (existingTags.includes(trimmed)) return prev;
      return { ...prev, tags: [...existingTags, trimmed] };
    });

    setErrors((prev) => {
      if (!prev.tags) return prev;
      const newErrors = { ...prev };
      delete newErrors.tags;
      return newErrors;
    });

    setTagInput('');
  }, [tagInput]);

  const handleTagRemove = useCallback((tagToRemove: string) => {
    setFormData((prev) => {
      const existingTags = (prev.tags || []) as string[];
      return {
        ...prev,
        tags: existingTags.filter((tag) => tag !== tagToRemove),
      };
    });
  }, []);

  const handleClearAllTags = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      tags: [],
    }));
  }, []);

  const handleDateTimeChange = useCallback(
    async (
      field: 'start_at' | 'end_at',
      date:
        | DateObject
        | Date
        | string
        | null
        | (DateObject | Date | string | null)[]
    ) => {
      if (!date) {
        setFormData((prev) => ({ ...prev, [field]: '' }));
        await validateField(field, '');
        setErrors((prev) => ({ ...prev, [field]: '' }));
        return;
      }

      const rawDate = Array.isArray(date) ? date[0] : date;

      if (!rawDate) {
        setFormData((prev) => ({ ...prev, [field]: '' }));
        await validateField(field, '');
        setErrors((prev) => ({ ...prev, [field]: '' }));
        return;
      }

      let jsDate: Date;
      if (rawDate instanceof Date) {
        jsDate = rawDate;
      } else if (typeof rawDate === 'string') {
        jsDate = new Date(rawDate);
      } else {
        const dateObject = rawDate as DateObject;
        jsDate = dateObject.toDate();
      }

      const isoString = jsDate.toISOString();

      setFormData((prev) => ({ ...prev, [field]: isoString }));
      await validateField(field, isoString);
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },
    [validateField, setFormData, setErrors]
  );

  const handleTimeInputChange = useCallback(
    (field: 'start_at' | 'end_at', timeValue: string) => {
      const [hoursStr, minutesStr] = timeValue.split(':');
      const hours = Number(hoursStr);
      const minutes = Number(minutesStr ?? '0');

      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return;
      }

      const baseDate =
        formData[field] && typeof formData[field] === 'string'
          ? new Date(formData[field] as string)
          : new Date();

      baseDate.setHours(hours, minutes, 0, 0);
      void handleDateTimeChange(field, baseDate);
    },
    [formData, handleDateTimeChange]
  );

  const handleMediaSave = useCallback(
    (updatedMedia: (File | string)[], updatedCaptions: ImageCaptionDTO[]) => {
      // Determine removed media
      const previousExistingUrls = mediaItems.filter(
        (item) => typeof item === 'string'
      ) as string[];
      const currentExistingUrls = updatedMedia.filter(
        (item) => typeof item === 'string'
      ) as string[];
      const removed = previousExistingUrls.filter(
        (url) => !currentExistingUrls.includes(url)
      );

      setRemovedMediaUrls((prev) => [...new Set([...prev, ...removed])]);
      setMediaItems(updatedMedia);
      setMediaPreview(
        updatedMedia.map((item) =>
          item instanceof File ? URL.createObjectURL(item) : item
        )
      );
      setImageCaptions(updatedCaptions);
    },
    [mediaItems]
  );

  const handleRemoveMedia = useCallback(
    (index: number) => {
      const itemToRemove = mediaItems[index];

      if (typeof itemToRemove === 'string') {
        // Removing existing media
        setRemovedMediaUrls((prev) => [...prev, itemToRemove]);
      }

      setMediaItems((prev) => prev.filter((_, i) => i !== index));
      setMediaPreview((prev) => prev.filter((_, i) => i !== index));

      // Update captions map and re-index
      setImageCaptions((prev) =>
        prev
          .filter((c) => c.image_index !== index)
          .map((c) => ({
            ...c,
            image_index:
              (c.image_index ?? 0) > index
                ? (c.image_index ?? 0) - 1
                : c.image_index,
          }))
      );
    },
    [mediaItems]
  );

  // Reset media when component unmounts
  useEffect(() => {
    return () => {
      setMediaPreview([]);
      setRemovedMediaUrls([]);
    };
  }, []);

  // Required fields for preview functionality
  const requiredFields = useMemo<(keyof IEventFormData)[]>(
    () => ['title', 'description', 'location_text'],
    []
  );

  const startTimeValue = useMemo(() => {
    if (!formData.start_at) return '';
    const date = new Date(formData.start_at);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }, [formData.start_at]);

  const endTimeValue = useMemo(() => {
    if (!formData.end_at) return '';
    const date = new Date(formData.end_at);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }, [formData.end_at]);

  // Check if all required fields are filled for preview
  useEffect(() => {
    const allRequiredFieldsFilled = requiredFields.every(
      (field: keyof IEventFormData) => !!formData[field]
    );

    // Preview should be available based only on core content fields,
    // regardless of other validation errors.
    setShowPreviewMessage(allRequiredFieldsFilled && !isReadOnly);
  }, [formData, requiredFields, isReadOnly]);

  // Helper function to ensure all languages are present in translations
  const ensureAllLanguages = useCallback(
    (
      translations: Record<string, IEventTranslation>
    ): Record<string, IEventTranslation> => {
      const completeTranslations: Record<string, IEventTranslation> = {};

      I18N_LANGUAGES.forEach((lang) => {
        completeTranslations[lang.code] = {
          title: translations[lang.code]?.title || formData.title || '',
          description:
            translations[lang.code]?.description || formData.description || '',
          location_text:
            translations[lang.code]?.location_text ||
            formData.location_text ||
            '',
        };
      });

      return completeTranslations;
    },
    [formData.title, formData.description, formData.location_text]
  );

  // Translation function
  const handleTranslateContent = useCallback(async (): Promise<
    Record<string, IEventTranslation>
  > => {
    try {
      const response = await translateEventContent({
        title: formData.title || '',
        description: formData.description || '',
        location_text: formData.location_text || '',
        source_language: currentLanguage.code,
      }).unwrap();

      const warningMessage = (response as { warning_message?: string })
        ?.warning_message;
      if (warningMessage?.trim()) {
        enqueueSnackbar(warningMessage, { variant: 'warning' });
      }

      const apiTranslations =
        (response.translations as Record<string, IEventTranslation>) || {};
      return ensureAllLanguages(apiTranslations);
    } catch (error: unknown) {
      console.error('Translation error:', error);
      enqueueSnackbar(
        intl.formatMessage({ id: 'EVENT.VALIDATION.TRANSLATION_ERROR' }) ||
        'Translation is temporarily unavailable. Saving with your entered text.',
        { variant: 'warning' }
      );
      return ensureAllLanguages({});
    }
  }, [
    formData.title,
    formData.description,
    formData.location_text,
    currentLanguage.code,
    translateEventContent,
    ensureAllLanguages,
    enqueueSnackbar,
    intl,
  ]);

  const onSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setErrors({});

      // Validate all fields
      const schema = createValidationSchema();
      await schema.validate(formData, { abortEarly: false });

      if (!communityId) {
        enqueueSnackbar(
          intl.formatMessage({ id: 'EVENT.VALIDATION.COMMUNITY_REQUIRED' }) ||
          'Community is required',
          { variant: 'error' }
        );
        return;
      }

      let eventTranslations: Record<string, IEventTranslation> = {};

      if (contentHasChanged) {
        eventTranslations = await handleTranslateContent();
      } else if (Object.keys(translations).length > 0) {
        eventTranslations = ensureAllLanguages(translations);
      } else {
        eventTranslations = await handleTranslateContent();
      }

      if (isEdit) {
        // Create FormData for API with media files
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title!);
        formDataToSend.append('description', formData.description!);
        formDataToSend.append('start_at', formData.start_at!);
        formDataToSend.append('end_at', formData.end_at!);

        if (formData.location_text) {
          formDataToSend.append('location_text', formData.location_text);
        }
        if (formData.latitude) {
          formDataToSend.append('latitude', formData.latitude.toString());
        }
        if (formData.longitude) {
          formDataToSend.append('longitude', formData.longitude.toString());
        }
        if (formData.timezone) {
          formDataToSend.append('timezone', formData.timezone);
        }
        if (formData.status) {
          formDataToSend.append('status', formData.status);
        }
        if (formData.language) {
          formDataToSend.append('language', formData.language);
        }
        if (formData.tags && formData.tags.length > 0) {
          formDataToSend.append('tags', JSON.stringify(formData.tags));
        }

        // Add media items to banner field in order
        if (mediaItems.length > 0) {
          mediaItems.forEach((item) => {
            formDataToSend.append('banner[]', item);
          });
        }

        // Add removed media URLs for deletion
        if (removedMediaUrls.length > 0) {
          formDataToSend.append(
            'removed_banner_images',
            JSON.stringify(removedMediaUrls)
          );
        }

        // Add image captions
        if (imageCaptions.length > 0) {
          formDataToSend.append(
            'image_captions',
            JSON.stringify(imageCaptions)
          );
        }

        if (eventTranslations && Object.keys(eventTranslations).length > 0) {
          formDataToSend.append(
            'translations',
            JSON.stringify(eventTranslations)
          );
        }

        const updateResult = await updateEvent({
          communityId,
          eventId: eventId!,
          data: formDataToSend,
        }).unwrap();

        const updateWarning = (updateResult as { warning_message?: string })
          ?.warning_message;
        if (updateWarning?.trim()) {
          enqueueSnackbar(updateWarning, { variant: 'warning' });
        }

        enqueueSnackbar(
          intl.formatMessage({ id: 'EVENT.VALIDATION.UPDATE_SUCCESS' }) ||
          'Event updated successfully',
          { variant: 'success' }
        );
      } else {
        // Create FormData for API with media files
        const formDataToSend = new FormData();
        formDataToSend.append('title', formData.title!);
        formDataToSend.append('description', formData.description!);
        formDataToSend.append('start_at', formData.start_at!);
        formDataToSend.append('end_at', formData.end_at!);

        if (formData.location_text) {
          formDataToSend.append('location_text', formData.location_text);
        }
        if (formData.latitude) {
          formDataToSend.append('latitude', formData.latitude.toString());
        }
        if (formData.longitude) {
          formDataToSend.append('longitude', formData.longitude.toString());
        }

        formDataToSend.append('language', currentLanguage.code);

        if (formData.tags && formData.tags.length > 0) {
          formDataToSend.append('tags', JSON.stringify(formData.tags));
        }

        // Add media items to banner field in order
        if (mediaItems.length > 0) {
          mediaItems.forEach((item) => {
            formDataToSend.append('banner[]', item);
          });
        }

        // Add image captions
        if (imageCaptions.length > 0) {
          formDataToSend.append(
            'image_captions',
            JSON.stringify(imageCaptions)
          );
        }

        if (eventTranslations && Object.keys(eventTranslations).length > 0) {
          formDataToSend.append(
            'translations',
            JSON.stringify(eventTranslations)
          );
        }

        const createResult = await createEvent({
          communityId,
          data: formDataToSend,
        }).unwrap();

        const createWarning = (createResult as { warning_message?: string })
          ?.warning_message;
        if (createWarning?.trim()) {
          enqueueSnackbar(createWarning, { variant: 'warning' });
        }

        enqueueSnackbar(
          intl.formatMessage({ id: 'EVENT.VALIDATION.CREATE_SUCCESS' }) ||
          'Event created successfully',
          { variant: 'success' }
        );
      }

      // Navigate back to where user came from (preserve community state)
      const referrer =
        location.state?.from || '/community/details?tab=event-promotion';
      navigate(referrer);
    } catch (error: unknown) {
      // Handle Yup validation errors
      if (error instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);
        return;
      }

      // Handle API errors
      let errorMessage = 'An unexpected error occurred';

      if (error && typeof error === 'object') {
        const apiError = error as {
          data?: { message?: string };
          message?: string;
        };
        if (apiError.data?.message) {
          errorMessage = apiError.data.message;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }

      enqueueSnackbar(
        intl.formatMessage({ id: 'EVENT.VALIDATION.SUBMIT_ERROR' }) ||
        errorMessage,
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    isEdit,
    eventId,
    communityId,
    currentLanguage.code,
    createValidationSchema,
    handleTranslateContent,
    createEvent,
    updateEvent,
    enqueueSnackbar,
    intl,
    navigate,
    location.state?.from,
    translations,
    mediaItems,
    removedMediaUrls,
    contentHasChanged,
    ensureAllLanguages,
    imageCaptions,
  ]);

  // Handler for opening preview modal
  const handleOpenPreview = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, []);

  // Handler for when translations are generated
  const handleTranslationsGenerated = useCallback(
    (newTranslations: Record<string, Partial<IEventFormData>>) => {
      // Convert to IEventTranslation format and store translations for form submission
      const convertedTranslations: Record<string, IEventTranslation> = {};
      Object.entries(newTranslations).forEach(([lang, data]) => {
        if (data.title && data.description) {
          convertedTranslations[lang] = {
            title: data.title,
            description: data.description,
            location_text: data.location_text,
          };
        }
      });
      setTranslations(convertedTranslations);
    },
    []
  );

  // Show loading state while fetching event data
  if (isEdit && isEventLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center h-64">
          <div className="loading loading-spinner loading-lg"></div>
          <span className="ml-2">Loading event data...</span>
        </div>
      </Container>
    );
  }

  // Show error state if event data fetch fails
  if (isEdit && isEventError) {
    return (
      <Container>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-500 mb-2">Failed to load event data</div>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-sm btn-light"
            >
              Retry
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="flex flex-col gap-4 mb-6 md:flex-row md:items-center md:justify-between text-nowrap">
        <h1 className="text-xl font-semibold">
          {isViewMode
            ? intl.formatMessage({ id: 'EVENT.TITLE.VIEW' }) || 'View Event'
            : isEdit
              ? intl.formatMessage({ id: 'EVENT.TITLE.EDIT' }) || 'Edit Event'
              : intl.formatMessage({ id: 'EVENT.TITLE.CREATE' }) ||
              'Create Event'}
        </h1>
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center md:justify-end">
          {/* Language Selector */}
          <div className="relative">
            <select
              className="select select-bordered pl-8 pr-8 h-10 leading-normal"
              value={currentLanguage.code}
              onChange={(e) => {
                const selectedLang = I18N_LANGUAGES.find(
                  (lang) => lang.code === e.target.value
                );
                if (selectedLang) {
                  changeLanguage(selectedLang);
                }
              }}
              aria-label="EVENT.FORM.LANGUAGE"
            >
              {I18N_LANGUAGES.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
            <img
              src="/media/flags/globe.svg"
              alt="Globe"
              className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
              aria-hidden="true"
            />
          </div>

          <div className="flex gap-2 flex-wrap md:ml-2 md:flex-shrink-0 justify-start md:justify-end">
            <button
              className="btn btn-light"
              type="button"
              onClick={() => {
                // Navigate back to where user came from (preserve community state)
                const referrer =
                  location.state?.from ||
                  '/community/details?tab=event-promotion';
                navigate(referrer);
              }}
              disabled={isSubmitting}
            >
              {isViewMode
                ? 'Back'
                : intl.formatMessage({ id: 'COMMON.CANCEL' }) || 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                className="btn btn-light"
                type="button"
                onClick={onSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    {isEdit
                      ? intl.formatMessage({ id: 'EVENT.LISTING.UPDATING' }) ||
                      'Updating...'
                      : intl.formatMessage({ id: 'EVENT.LISTING.CREATING' }) ||
                      'Creating...'}
                  </>
                ) : isEdit ? (
                  intl.formatMessage({ id: 'EVENT.FORM.UPDATE_EVENT' }) ||
                  'Update Event'
                ) : (
                  intl.formatMessage({ id: 'EVENT.FORM.CREATE_EVENT' }) ||
                  'Create Event'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Media Upload */}
            {!isReadOnly && (
              <div className="w-full lg:col-span-2">
                <span
                  id="event-media-upload-label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {intl.formatMessage({ id: 'EVENT.FORM.IMAGE' }) ||
                    'Event Media'}
                </span>
                <button
                  type="button"
                  onClick={() => setIsMediaModalOpen(true)}
                  disabled={isSubmitting}
                  className="btn btn-sm btn-light flex items-center gap-2"
                  aria-labelledby="event-media-upload-button-text event-media-upload-label"
                >
                  {/* <KeenIcon icon="upload" className="w-4 h-4" /> */}
                  <span id="event-media-upload-button-text">
                    {intl.formatMessage({ id: 'EVENT.LISTING.IMAGE_UPLOAD' }) ||
                      'Choose Images'}
                  </span>
                </button>
                <MediaUploadModal
                  open={isMediaModalOpen}
                  onClose={() => setIsMediaModalOpen(false)}
                  onSave={handleMediaSave}
                  allowedTypes={[
                    'image/jpeg',
                    'image/png',
                    'image/jpg',
                    'image/svg+xml',
                  ]}
                  maxFiles={1}
                  initialMedia={mediaItems}
                  initialCaptions={imageCaptions}
                />
              </div>
            )}

            {/* Media Preview */}
            {mediaPreview.length > 0 && (
              <div className="w-full lg:col-span-2">
                <span
                  id="event-media-preview-label"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Media Preview ({mediaPreview.length}{' '}
                  {mediaPreview.length === 1 ? 'file' : 'files'})
                </span>
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                  aria-labelledby="event-media-preview-label"
                >
                  {mediaItems.map((item, index) => {
                    const preview = mediaPreview[index];
                    const isExisting = typeof item === 'string';
                    const isRemoved = isExisting
                      ? removedMediaUrls.includes(item as string)
                      : false;

                    const hasCaption = imageCaptions.find(
                      (c) => c.image_index === index
                    );

                    return (
                      <div
                        key={index}
                        className={clsx(
                          'relative rounded-lg overflow-hidden group',
                          isRemoved && 'opacity-50'
                        )}
                      >
                        <img
                          src={preview}
                          alt={`Media ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        {/* Badge for existing vs new */}
                        <div className="absolute top-2 left-2 flex flex-col gap-1">
                          <span
                            className={clsx(
                              'px-2 py-1 text-xs font-semibold rounded-full',
                              isExisting
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            )}
                          >
                            {isExisting ? 'Existing' : 'New'}
                          </span>
                          {hasCaption && (
                            <span className="bg-purple-100 text-purple-800 px-2 py-1 text-xs font-semibold rounded-full">
                              {intl.formatMessage({
                                id: 'COMMON.HAS_CAPTIONS',
                              }) || 'Has Captions'}
                            </span>
                          )}
                        </div>
                        {/* Remove button (only in edit mode) */}
                        {mode === 'edit' && !isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMedia(index)}
                            className="absolute top-1 right-1 btn btn-xs btn-error opacity-0 group-hover:opacity-100 transition-opacity"
                            title={isRemoved ? 'Will be removed' : 'Remove'}
                          >
                            ×
                          </button>
                        )}
                        {/* Strikethrough effect for removed items */}
                        {isRemoved && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                            <div className="w-full h-1 bg-red-500 transform rotate-45"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Title */}
            <div className="w-full">
              <label
                htmlFor="title-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.TITLE' }) ||
                  'Event Title'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                id="title-input"
                type="text"
                name="title"
                autoComplete="off"
                value={formData.title || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isReadOnly || isSubmitting}
                readOnly={isReadOnly}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.title && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'title-error' : undefined}
                placeholder={
                  intl.formatMessage({ id: 'EVENT.FORM.TITLE' }) ||
                  'Enter event title'
                }
              />
              {errors.title && (
                <p
                  id="title-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.title}
                </p>
              )}
            </div>

            {/* Location */}
            <div className="w-full">
              <label
                htmlFor="location-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.LOCATION' }) ||
                  'Location'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                id="location-input"
                type="text"
                name="location_text"
                autoComplete="off"
                value={formData.location_text || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isReadOnly || isSubmitting}
                readOnly={isReadOnly}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300',
                  errors.location_text && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                aria-invalid={!!errors.location_text}
                aria-describedby={
                  errors.location_text ? 'location-error' : undefined
                }
                placeholder={
                  intl.formatMessage({ id: 'EVENT.FORM.LOCATION' }) ||
                  'Enter event location'
                }
              />
              {errors.location_text && (
                <p
                  id="location-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.location_text}
                </p>
              )}
            </div>

            {/* Latitude */}
            <div className="w-full">
              <label
                htmlFor="latitude-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.LATITUDE' }) ||
                  'Latitude'}
              </label>
              <NumberInput
                id="latitude-input"
                name="latitude"
                autoComplete="off"
                value={formData.latitude ?? ''}
                onChange={handleInputChange}
                onBlur={(e) =>
                  handleInputBlur(
                    e.target.name,
                    e.target.value === '' ? '' : parseFloat(e.target.value)
                  )
                }
                disabled={isReadOnly || isSubmitting}
                readOnly={isReadOnly}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                  errors.latitude && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                aria-invalid={!!errors.latitude}
                aria-describedby={
                  errors.latitude ? 'latitude-error' : undefined
                }
                placeholder={`${intl.formatMessage({ id: 'EVENT.FORM.LATITUDE' }) || 'Latitude'} (-90 to 90)`}
                step="0.0001"
                min="-90"
                max="90"
                maxDecimals={100}
              />
              {errors.latitude && (
                <p
                  id="latitude-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.latitude}
                </p>
              )}
            </div>

            {/* Longitude */}
            <div className="w-full">
              <label
                htmlFor="longitude-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.LONGITUDE' }) ||
                  'Longitude'}
              </label>
              <NumberInput
                id="longitude-input"
                name="longitude"
                autoComplete="off"
                value={formData.longitude ?? ''}
                onChange={handleInputChange}
                onBlur={(e) =>
                  handleInputBlur(
                    e.target.name,
                    e.target.value === '' ? '' : parseFloat(e.target.value)
                  )
                }
                disabled={isReadOnly || isSubmitting}
                readOnly={isReadOnly}
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                  errors.longitude && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                aria-invalid={!!errors.longitude}
                aria-describedby={
                  errors.longitude ? 'longitude-error' : undefined
                }
                placeholder={`${intl.formatMessage({ id: 'EVENT.FORM.LONGITUDE' }) || 'Longitude'} (-180 to 180)`}
                step="0.0001"
                min="-180"
                max="180"
                maxDecimals={100}
              />
              {errors.longitude && (
                <p
                  id="longitude-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.longitude}
                </p>
              )}
            </div>

            {/* Start Date */}
            <div className="w-full">
              <label
                htmlFor="start-date-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.START_DATE' }) ||
                  'Start Date'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <DatePicker
                id="start-date-input"
                value={formData.start_at ? new Date(formData.start_at) : null}
                onChange={(date) => {
                  void handleDateTimeChange('start_at', date);
                }}
                format="DD/MM/YYYY"
                disabled={isReadOnly || isSubmitting}
                calendarPosition="bottom-start"
                hideOnScroll={true}
                editable
                containerClassName="w-full"
                inputClass={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 text-start',
                  errors.start_at && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                placeholder="DD/MM/YYYY HH:mm"
                aria-required={true}
                aria-invalid={!!errors.start_at}
                aria-describedby={
                  errors.start_at ? 'start-date-error' : undefined
                }
              />
              <input
                id="start-time-input"
                type="time"
                name="start_time"
                autoComplete="off"
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 mt-2',
                  errors.start_at && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                disabled={isReadOnly || isSubmitting}
                value={startTimeValue}
                onChange={(e) =>
                  handleTimeInputChange('start_at', e.target.value)
                }
                aria-label={
                  intl.messages['EVENT.FORM.START_TIME']
                    ? intl.formatMessage({ id: 'EVENT.FORM.START_TIME' })
                    : 'Start time'
                }
              />
              {errors.start_at && (
                <p
                  id="start-date-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.start_at}
                </p>
              )}
            </div>

            {/* End Date */}
            <div className="w-full">
              <label
                htmlFor="end-date-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.END_DATE' }) ||
                  'End Date'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <DatePicker
                id="end-date-input"
                value={formData.end_at ? new Date(formData.end_at) : null}
                onChange={(date) => {
                  void handleDateTimeChange('end_at', date);
                }}
                format="DD/MM/YYYY"
                disabled={isReadOnly || isSubmitting}
                calendarPosition="bottom-start"
                hideOnScroll={true}
                containerClassName="w-full"
                inputClass={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 text-start',
                  errors.end_at && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                placeholder="DD/MM/YYYY HH:mm"
                minDate={
                  formData.start_at ? new Date(formData.start_at) : undefined
                }
                aria-required={true}
                aria-invalid={!!errors.end_at}
                aria-describedby={errors.end_at ? 'end-date-error' : undefined}
              />
              <input
                id="end-time-input"
                type="time"
                name="end_time"
                autoComplete="off"
                className={clsx(
                  'input input-bordered w-full h-11 rounded-lg border-gray-300 mt-2',
                  errors.end_at && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                disabled={isReadOnly || isSubmitting}
                value={endTimeValue}
                onChange={(e) =>
                  handleTimeInputChange('end_at', e.target.value)
                }
                aria-label={
                  intl.messages['EVENT.FORM.END_TIME']
                    ? intl.formatMessage({ id: 'EVENT.FORM.END_TIME' })
                    : 'End time'
                }
              />
              {errors.end_at && (
                <p
                  id="end-date-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.end_at}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="w-full lg:col-span-2">
              <label
                htmlFor="description-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'EVENT.FORM.DESCRIPTION' }) ||
                  'Description'}{' '}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description-input"
                name="description"
                value={formData.description || ''}
                onChange={handleInputChange}
                onBlur={(e) => handleInputBlur(e.target.name, e.target.value)}
                disabled={isReadOnly || isSubmitting}
                readOnly={isReadOnly}
                className={clsx(
                  'textarea textarea-bordered w-full rounded-lg border-gray-300',
                  errors.description && 'border-red-500',
                  isReadOnly && 'bg-gray-50 cursor-not-allowed'
                )}
                aria-invalid={!!errors.description}
                aria-describedby={
                  errors.description ? 'description-error' : undefined
                }
                placeholder={
                  intl.formatMessage({ id: 'EVENT.FORM.DESCRIPTION' }) ||
                  'Enter event description'
                }
                rows={4}
              />
              {errors.description && (
                <p
                  id="description-error"
                  className="text-red-500 text-xs mt-1"
                  role="alert"
                >
                  {errors.description}
                </p>
              )}
            </div>

            {/* Tags */}
            <div className="w-full lg:col-span-2">
              <label
                htmlFor="tags-input"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {intl.formatMessage({ id: 'POI.FORM.TAGS' }) || 'Tags'}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <div className="relative flex-1">
                  <input
                    id="tags-input"
                    type="text"
                    name="tags"
                    autoComplete="off"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' && (e.preventDefault(), handleTagAdd())
                    }
                    disabled={isReadOnly || isSubmitting}
                    className={clsx(
                      'input input-bordered w-full h-11 rounded-lg border-gray-300 pr-24',
                      errors.tags && 'border-red-500'
                    )}
                    placeholder={
                      intl.formatMessage({ id: 'POI.FORM.ADD_TAG' }) ||
                      'Add tag'
                    }
                  />
                  {(formData.tags || []).length > 0 && (
                    <button
                      type="button"
                      onClick={handleClearAllTags}
                      disabled={isReadOnly || isSubmitting}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                    >
                      {intl.formatMessage({ id: 'POI.FORM.CLEAR_ALL' }) ||
                        'Clear all'}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleTagAdd}
                  disabled={!tagInput.trim() || isReadOnly || isSubmitting}
                  className="btn btn-sm h-11 btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
                >
                  {intl.formatMessage({ id: 'POI.FORM.ADD_TAG' }) || 'Add tag'}
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {(formData.tags || []).map((tag) => (
                  <div
                    key={tag}
                    className="flex items-center bg-blue-100 dark:bg-blue-900 contrast:bg-blue-700 text-blue-800 dark:text-blue-200 contrast:text-white text-sm font-medium px-2.5 py-0.5 rounded space-x-[2px]"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      disabled={isReadOnly || isSubmitting}
                      className="text-blue-800 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-400 ml-1"
                      aria-label={`Remove ${tag} tag`}
                    >
                      <KeenIcon
                        type="filled"
                        icon="cross"
                        className="w-3 h-3"
                      />
                    </button>
                  </div>
                ))}
              </div>
              {errors.tags && (
                <p className="text-red-500 text-xs mt-1" role="alert">
                  {errors.tags}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Preview Message */}
        {showPreviewMessage && !isReadOnly && (
          <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-700 rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start sm:items-center">
              <KeenIcon
                type="outline"
                icon="paper-clip"
                className="w-5 h-5 text-blue-600 dark:text-blue-200 contrast:text-blue-200 mr-2"
              />
              <p className="text-blue-700 dark:text-blue-200 contrast:text-blue-200 text-sm flex-1">
                {intl.formatMessage({ id: 'EVENT.PREVIEW.NOTICE' }) ||
                  'All required fields are filled. You can preview your event content in different languages.'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleOpenPreview}
              className="btn btn-lg bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
              aria-label={
                intl.formatMessage({
                  id: 'EVENT.BUTTON.SHOW_PREVIEW',
                }) || 'Show preview'
              }
            >
              {intl.formatMessage({ id: 'EVENT.BUTTON.SHOW_PREVIEW' }) ||
                'Show Preview'}
            </button>
          </div>
        )}

        {/* Preview Modal */}
        <EventPreviewModal
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          formData={formData}
          source_language={currentLanguage.code}
          contentHasChanged={contentHasChanged}
          onTranslationsGenerated={handleTranslationsGenerated}
        />
      </div>

      {/* RSVP List Section - Only visible in view mode */}
      {isViewMode && eventId && (
        <div className="mt-5">
          <EventRsvpList eventId={eventId} />
        </div>
      )}
    </Container>
  );
};

export default EventForm;
